import { useTranslation } from "react-i18next"
import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { convertGender } from "@/utils/format"
import { getSportLabel } from "@/constants/sportConfig"
import ImageFallback from "@/app/components/ImageFallback"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import useRouter from "@/hooks/router/useRoute"

const cellImage = "/images/cell_bx_black.png"
const dockImage = "/images/dock.png"
const liveHubImage = "/images/live_hub.png"

export default function TeamSummaryWidget() {
  const { t } = useTranslation()
  const router = useRouter()
  const teamProfileInfo = createAccountStore((state) => state.teamProfileInfo)
  const isPlayerAccount = createAccountStore((state) => state.info?.type === AccountTypeKey.Player)

  if (!teamProfileInfo) {
    return null
  }

  const details = [
    getSportLabel(teamProfileInfo.sportType),
    teamProfileInfo.country,
    teamProfileInfo.groupAge,
    teamProfileInfo.gender && convertGender(teamProfileInfo.gender),
    teamProfileInfo.division,
  ].filter(Boolean) as string[]

  const members = [
    {
      key: "players",
      icon: "team-line" as const,
      path: "/settings/club/players",
      count: teamProfileInfo.playerCount ?? 0,
    },
    {
      key: "coaches",
      icon: "user-voice-line" as const,
      path: "/settings/club/coaches",
      count: teamProfileInfo.coachCount ?? 0,
    },
  ]

  const equipments = [
    {
      key: "cells",
      image: cellImage,
      count: teamProfileInfo.cellCount ?? 0,
    },
    {
      key: "docks",
      image: dockImage,
      count: teamProfileInfo.dockCount ?? 0,
    },
    {
      key: "liveHubs",
      image: liveHubImage,
      count: teamProfileInfo.hubCount ?? 0,
    },
  ]

  const clickable = !isPlayerAccount
  const handleMemberClick = (path: string) => clickable && router?.push(path)
  const handleEquipmentClick = () => clickable && router?.push("/settings/club/equipments")

  return (
    <div className="TeamSummaryWidget">
      <div className="TeamSummaryWidget__inner">
        <div className="TeamSummaryWidget__team-info">
          <ImageFallback
            image={teamProfileInfo.image}
            iconType="no-team"
            size={48}
            alt={teamProfileInfo.name}
          />
          <div className="TeamSummaryWidget__team-name">
            <h3>{teamProfileInfo.name}</h3>
            {details.length > 0 && (
              <div className="TeamSummaryWidget__details">
                {details.map((label, i) => (
                  <span key={`${label}-${i}`}>{label}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="TeamSummaryWidget__stats-section">
          <div className="TeamSummaryWidget__stats-group TeamSummaryWidget__stats-group--members">
            {members.map((member) => (
              <div
                key={member.key}
                className={`TeamSummaryWidget__stat-item ${clickable ? "TeamSummaryWidget__stat-item--clickable" : ""}`}
                onClick={() => handleMemberClick(member.path)}>
                <div className="TeamSummaryWidget__stat-icon">
                  <Icon type={member.icon} size={20} color={colors.neutral.platinum[500]} />
                </div>
                <div className="TeamSummaryWidget__stat-content">
                  <span className="TeamSummaryWidget__stat-label">{t(member.key)}</span>
                  <span className="TeamSummaryWidget__stat-value">{member.count}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="TeamSummaryWidget__divider" />

          <div className="TeamSummaryWidget__stats-group TeamSummaryWidget__stats-group--equipments">
            {equipments.map((eq) => (
              <div
                key={eq.key}
                className={`TeamSummaryWidget__stat-item ${clickable ? "TeamSummaryWidget__stat-item--clickable" : ""}`}
                onClick={handleEquipmentClick}>
                <div className="TeamSummaryWidget__stat-icon">
                  <img src={eq.image} alt={eq.key} />
                </div>
                <div className="TeamSummaryWidget__stat-content">
                  <span className="TeamSummaryWidget__stat-label">{t(eq.key)}</span>
                  <span className="TeamSummaryWidget__stat-value">{eq.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
